import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cadenceLabel, formatUseCase, CARD_BASE } from './scheduleFormat';

const CARD = `${CARD_BASE} p-4`;

const HorizonBadge = ({ label }) => (
  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
    {label}
  </span>
);

const PreviewBadge = () => (
  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800/60">
    Unpaid · preview
  </span>
);

// Plain-language meaning of each horizon, shown between the badge and cadence.
const horizonMeaning = (row) => {
  if (row.challenge_type === 'intraday') {
    return row.intraday_hours
      ? `Rolling forecast of the next ${row.intraday_hours}h, starting at each gate closure.`
      : 'Rolling intraday forecast, starting at each gate closure.';
  }
  if (row.horizon_offset === 1) {
    return 'Day-ahead forecast for the next calendar day.';
  }
  return `Forecast for D+${row.horizon_offset}, ${row.horizon_offset} days ahead.`;
};

// One card per (resource, use case); each lists its horizons and cadence.
const groupByResource = (patterns) => {
  const map = new Map();
  for (const p of patterns) {
    const key = `${p.resource}|${p.use_case}`;
    if (!map.has(key)) {
      map.set(key, {
        resource: p.resource,
        resourceName: p.resource_name,
        useCase: p.use_case,
        rows: [],
      });
    }
    map.get(key).rows.push(p);
  }
  for (const group of map.values()) {
    group.rows.sort((a, b) => a.horizon_offset - b.horizon_offset);
  }
  return [...map.values()].sort((a, b) =>
    a.resourceName.localeCompare(b.resourceName)
  );
};

export default function RhythmPanel({ patterns = [], loading = false }) {
  const groups = groupByResource(patterns);

  // Each resource card is collapsed by default: a resource with many horizons
  // stretches the panel a lot vertically, so we let the user expand only the
  // ones they care about. Keyed by `resource|useCase`.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Recurring rhythm
        </h2>
        <p className="mt-1 text-[11px] leading-4 text-gray-400 dark:text-gray-500">
          How often challenges open for this resource, with each session's open
          and gate-closure times.
        </p>
      </div>
      {loading && groups.length === 0 ? (
        <div className={`${CARD} text-sm text-gray-500 dark:text-gray-400`}>
          Loading schedule…
        </div>
      ) : groups.length === 0 ? (
        <div className={`${CARD} text-sm text-gray-500 dark:text-gray-400`}>
          No active challenge templates to project.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {groups.map((group) => {
            const key = `${group.resource}|${group.useCase}`;
            const isOpen = expanded.has(key);
            return (
            <div
              key={key}
              className={CARD}
              data-testid="rhythm-card"
            >
              {/* Header toggles the card on mobile; on lg+ the body is always
                  shown (lg:block below) so the fold control is hidden and the
                  click is a no-op. */}
              <button
                type="button"
                onClick={() => toggle(key)}
                aria-expanded={isOpen}
                className={`flex w-full items-baseline justify-between gap-2 text-left lg:cursor-default ${isOpen ? 'mb-2' : ''} lg:mb-2`}
              >
                <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {group.resourceName}
                </h3>
                <span className="flex shrink-0 items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatUseCase(group.useCase)}</span>
                  <span className="text-gray-300 dark:text-gray-600 lg:hidden">·</span>
                  <span className="lg:hidden">{group.rows.length} horizon{group.rows.length !== 1 ? 's' : ''}</span>
                  <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-gray-500 lg:hidden ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </button>
              <div
                data-testid="rhythm-body"
                className={`${isOpen ? 'block' : 'hidden'} lg:block`}
              >
              <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {group.rows.map((row) => (
                  <li
                    key={row.horizon_label}
                    className="flex flex-col gap-1 py-2 sm:flex-row sm:items-baseline sm:gap-3 sm:py-1.5"
                  >
                    <div className="w-20 shrink-0">
                      <HorizonBadge label={row.horizon_label} />
                    </div>
                    <p className="min-w-0 flex-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                      {horizonMeaning(row)}
                      {!row.is_paid && (
                        <span className="ml-1.5 inline-block align-middle">
                          <PreviewBadge />
                        </span>
                      )}
                    </p>
                    <span className="shrink-0 text-xs text-gray-600 dark:text-gray-300 sm:text-right">
                      {cadenceLabel(row)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] leading-4 text-gray-400 dark:text-gray-500">
                Each session opens an hour before its gate closure. Submit
                Q10/Q50/Q90 for every timestamp before gate closure. Day-ahead
                horizons score only if you submit at every session of the target
                day.
              </p>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
